import React from 'react';

import {Divider, Typography} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import SyncIcon from 'spectra-logic-ui/icons/SyncAlt';
import ExportIcon from 'spectra-logic-ui/icons/TrendingFlat';

const useStyles = makeStyles({
  cloudIcon: {
    width: 20,
    marginRight: 7,
  },
  divider: {
    margin: '10px 0',
  },
  example: {
    marginBottom: 10,
  },
  exampleHeader: {
    fontStyle: 'italic',
    marginBottom: 2,
  },
  exampleContent: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      marginLeft: 10,
    },
  },
  exampleItem: {
    display: 'flex',
    alignItems: 'center',
  },
});

const ImportIcon = (props: any) => <ExportIcon style={{transform: 'scale(-1, 1)'}} {...props} />;

export const AwsStoreTooltip = () => {
  const classes = useStyles();
  return (
    <div>
      <Typography variant='body2'>
        Cloud buckets can be used as storage placements when defining lifecycle rules.
        Each object from the system bucket will be stored in the cloud bucket with a prefix added
        using the originating bucket's name.
      </Typography>
      <Divider className={classes.divider} />
      <div className={classes.example}>
        <div className={classes.exampleHeader}>
          <Typography variant='body2' display='inline'>
            <strong>Example: </strong>
          </Typography>
        </div>
        <div className={classes.exampleContent}>
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>system-bucket-1/file.txt</strong>
            </Typography>
          </div>
          <ExportIcon />
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>cloud-bucket/system-bucket-1/file.txt</strong>
            </Typography>
          </div>
        </div>
        <div className={classes.exampleContent}>
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>system-bucket-2/file.txt</strong>
            </Typography>
          </div>
          <ExportIcon />
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>cloud-bucket/system-bucket-2/file.txt</strong>
            </Typography>
          </div>
        </div>
        <div className={classes.exampleContent}>
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>system-bucket-3/file.txt</strong>
            </Typography>
          </div>
          <ExportIcon />
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>cloud-bucket/system-bucket-3/file.txt</strong>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AwsLinkTooltip = () => {
  const classes = useStyles();
  return (
    <div>
      <Typography variant='body2'>
        When you've selected a single bucket to use with this storage, you can choose to import the storage
        content. The import creates objects in the bucket you've selected corresponding to objects on this storage.
        The import doesn't copy any data, the created objects just reference this storage as their data source.
        A lifecycle can by used to copy data to local storage, if desired. By including this storage as a
        destination in a lifecycle, you can "synchronize" the selected bucket with the storage. Synchronizing
        allows a bucket to maintain consistency with an external bucket, regardless of whether objects are added
        locally or to the external bucket.
      </Typography>
      <Divider className={classes.divider} />
      <div className={classes.example}>
        <div className={classes.exampleHeader}>
          <Typography variant='body2' display='inline'><strong>Example 1: </strong></Typography>
          <Typography variant='body2' display='inline'>Synchronizing via lifecycle</Typography>
        </div>
        <div className={classes.exampleContent}>
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>System Bucket</strong>
            </Typography>
          </div>
          <SyncIcon />
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>Storage Cloud Bucket</strong>
            </Typography>
          </div>
        </div>
      </div>
      <div className={classes.example}>
        <div className={classes.exampleHeader}>
          <Typography variant='body2' display='inline'><strong>Example 2: </strong></Typography>
          <Typography variant='body2' display='inline'>Importing</Typography>
        </div>
        <div className={classes.exampleContent}>
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>System Bucket</strong>
            </Typography>
          </div>
          <ImportIcon />
          <div className={classes.exampleItem}>
            <Typography variant='body2' display='inline'>
              <strong>Storage Cloud Bucket</strong>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export const AddressingStyleTooltip = () => {
  const classes = useStyles();
  return (
    <div>
      <Typography variant='body2' display='inline'>
        Addressing style controls the URL format used when communicating with the remote cloud provider.
      </Typography>
      <Divider className={classes.divider} />
      <div className={classes.example}>
        <div className={classes.exampleHeader}>
          <Typography variant='body2' display='inline'><strong>Path Style</strong></Typography>
        </div>
        <div className={classes.exampleContent}>
          <Typography variant='body2' display='inline'>
            Path style addressing is the default and includes the bucket name as part of the URL path.
            <div className={classes.exampleItem}>
              <i>http://endpoint/bucket-name/object-key</i>
            </div>
          </Typography>
        </div>
        <div className={classes.exampleHeader}>
          <Typography variant='body2' display='inline'><strong>Virtual-Hosted Style</strong></Typography>
        </div>
        <div className={classes.exampleContent}>
          <Typography variant='body2' display='inline'>
            Virtual-hosted style addressing prefixes the endpoint with the bucket name.
            <div className={classes.exampleItem}>
              <i>http://bucket-name.endpoint/object-key</i>
            </div>
          </Typography>
        </div>
      </div>
    </div>
  );
};
